import { createClient } from '@supabase/supabase-js';
import type { Database, Json } from './types';
import { getSupabaseAuthUrl } from '@/lib/supabaseInfra';

type CuponRow = {
  id: string;
  slug: string | null;
  titulo: string;
  descripcion: string | null;
  descripcion_detallada: string | null;
  descuento_porcentaje: number | null;
  descuento_monto: number | null;
  codigo_promocional: string;
  fecha_inicio: string;
  fecha_fin: string;
  activa: boolean;
  color_fondo: string | null;
  color_texto: string | null;
  color_acento: string | null;
  terminos_condiciones: string | null;
  limite_usos: number | null;
  usos_actuales: number;
  tipo_promocion: string | null;
  publico_objetivo: string | null;
  tipo_operacion: string | null;
  comision: number | null;
  created_at: string;
  updated_at: string;
  created_by: string;
};

type RentalProcessRow = {
  id: string;
  property_id: string | null;
  client_name: string;
  client_email: string;
  status: 'active' | 'completed' | 'cancelled';
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

type RentalApplicationLinkRow = {
  id: string;
  rental_process_id: string;
  application_type: 'personal' | 'corporate' | 'obligado_solidario' | 'owner';
  application_id: string | null;
  slug: string;
  link_url: string;
  status: 'active' | 'completed' | 'expired';
  expires_at: string | null;
  created_at: string;
  used_at: string | null;
};

type ExtendedDatabase = Database & {
  public: Database['public'] & {
    Tables: Database['public']['Tables'] & {
      cupones: {
        Row: CuponRow;
        Insert: Partial<CuponRow> & Pick<CuponRow, 'titulo' | 'codigo_promocional' | 'fecha_inicio' | 'fecha_fin'>;
        Update: Partial<CuponRow>;
        Relationships: [];
      };
      rental_processes: {
        Row: RentalProcessRow;
        Insert: Partial<RentalProcessRow> & Pick<RentalProcessRow, 'client_name' | 'client_email'>;
        Update: Partial<RentalProcessRow>;
        Relationships: [];
      };
      rental_application_links: {
        Row: RentalApplicationLinkRow;
        Insert: Partial<RentalApplicationLinkRow> & Pick<RentalApplicationLinkRow, 'rental_process_id' | 'application_type' | 'slug' | 'link_url'>;
        Update: Partial<RentalApplicationLinkRow>;
        Relationships: [];
      };
    };
    Views: Database['public']['Views'] & {
      rental_processes_summary: {
        Row: RentalProcessRow & {
          property_title: string | null;
          property_address: string | null;
          property_price: number | null;
          applications_summary:
            | {
                personal_applications: number;
                corporate_applications: number;
                obligado_solidario_applications: number;
                total_applications: number;
              }
            | string
            | null;
        };
        Relationships: [];
      };
    };
    Functions: Database['public']['Functions'] & {
      favorites_count_myself: {
        Args: Record<string, never>;
        Returns: number;
      };
      rpc_admin_notification_in_app_catalog_state_v1: {
        Args: Record<string, never>;
        Returns: Array<{
          channel_code: string;
          default_enabled: boolean;
          description: string;
          effective_is_enabled: boolean;
          explicit_created_at: string | null;
          explicit_is_enabled: boolean | null;
          explicit_preference_id: number | null;
          explicit_updated_at: string | null;
          is_user_configurable: boolean;
          label: string;
          matched_notification_code: string | null;
          notification_code: string;
          resolution_source: string;
          sort_order: number;
        }>;
      };
      rpc_admin_notification_channel_preferences: {
        Args: Record<string, never>;
        Returns: Array<{
          channel_code: string;
          created_at: string;
          id: number;
          is_enabled: boolean;
          notification_code: string;
          updated_at: string;
        }>;
      };
      rpc_admin_notification_channel_preference_upsert: {
        Args: {
          p_channel_code: string;
          p_is_enabled: boolean;
          p_notification_code: string;
        };
        Returns: Array<{
          channel_code: string;
          created_at: string;
          id: number;
          is_enabled: boolean;
          notification_code: string;
          updated_at: string;
        }>;
      };
      rpc_admin_notification_channel_preference_delete: {
        Args: {
          p_channel_code: string;
          p_notification_code: string;
        };
        Returns: Json;
      };
      rpc_admin_notification_push_subscriptions: {
        Args: Record<string, never>;
        Returns: Array<{
          browser_name: string | null;
          browser_version: string | null;
          created_at: string;
          device_label: string | null;
          disabled_at: string | null;
          endpoint: string;
          expiration_time: string | null;
          id: number;
          is_active: boolean;
          last_seen_at: string;
          locale: string | null;
          permission_state: string;
          platform: string | null;
          updated_at: string;
          user_agent: string | null;
        }>;
      };
      rpc_admin_notification_push_subscription_upsert: {
        Args: {
          p_auth: string;
          p_browser_name?: string | null;
          p_browser_version?: string | null;
          p_device_label?: string | null;
          p_endpoint: string;
          p_expiration_time?: string | null;
          p_locale?: string | null;
          p_p256dh: string;
          p_permission_state?: string;
          p_platform?: string | null;
          p_user_agent?: string | null;
        };
        Returns: Array<{
          browser_name: string | null;
          browser_version: string | null;
          created_at: string;
          device_label: string | null;
          disabled_at: string | null;
          endpoint: string;
          expiration_time: string | null;
          id: number;
          is_active: boolean;
          last_seen_at: string;
          locale: string | null;
          permission_state: string;
          platform: string | null;
          updated_at: string;
          user_agent: string | null;
        }>;
      };
      rpc_admin_notification_push_subscription_disable: {
        Args: {
          p_endpoint?: string | null;
          p_permission_state?: string;
          p_subscription_id?: number | null;
        };
        Returns: Json;
      };
      track_property_view: {
        Args: {
          p_browser?: string;
          p_device_type?: string;
          p_ip_address?: unknown;
          p_property_id: string;
          p_referrer?: string;
          p_session_id?: string;
          p_user_agent?: string;
          p_utm_campaign?: string;
          p_utm_medium?: string;
          p_utm_source?: string;
          p_view_duration?: number;
        };
        Returns: Json;
      };
      rpc_get_source_channel_metrics_overview_v1: {
        Args: {
          p_period_key?: string;
          p_scope?: string;
          p_actor_id?: string | null;
        };
        Returns: Json;
      };
      rpc_get_my_lead_activity_metrics_v1: {
        Args: {
          p_period_key?: string;
        };
        Returns: Json;
      };
    };
  };
};

// Extiende SupabaseClient para incluir tus funciones RPC personalizadas (opcional)
declare module '@supabase/supabase-js' {
  interface SupabaseClient {
    rpc(
      fn: 'get_location_hierarchy',
      params: { location_id: number }
    ): Promise<{ data: unknown; error: unknown }>;
    rpc(
      fn: 'search_locations_with_hierarchy',
      params: { search_term: string }
    ): Promise<{ data: unknown; error: unknown }>;
    rpc(
      fn: 'properties_within_distance',
      params: { lat: number; lng: number; radius?: number }
    ): Promise<{ data: unknown[] | null; error: unknown }>;
  }
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
}
const GOTRUE_URL = getSupabaseAuthUrl();

export const supabase = createClient<ExtendedDatabase>(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    // PKCE is recommended for SPAs and avoids third-party cookie issues
    flowType: 'pkce',
    autoRefreshToken: true,
    persistSession: true,
    // Let the SDK auto-detect and exchange the code from the URL (prevents double-exchange issues)
    detectSessionInUrl: true,
    // Persist under a stable storage key
    storageKey: 'sb-raiz-auth',
    // If provided, override Auth URL with the full GoTrue endpoint (INCLUDE /auth/v1)
    ...(GOTRUE_URL ? { url: GOTRUE_URL } : {}),
  },
});
